import { Form, Formik, FormikHelpers } from 'formik';

import { notifyError, notifySuccess } from '@/portainer/services/notifications';

import { LoadingButton } from '@@/buttons';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { TextArea } from '@@/form-components/Input/Textarea';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { Select } from '@@/form-components/ReactSelect';
import { Alert } from '@@/Alert';

import {
  AlertRuleRow,
  AlertRuleTier,
  AlertRuleTiersPayload,
  MetricType,
} from '../../types';
import {
  useUpdateAlertRule,
  useUpdateAlertRuleTiers,
} from '../AlertRulesDatatable/useAlertRules';
import { ThresholdInput } from '../AlertRulesDatatable/ThresholdInput';
import { canonicalizeAlertRuleTiers } from '../../utils';

import { TierConfigPanel } from './TierConfigPanel';
import { createValidationSchema } from './alertRuleValidation';

interface Props {
  alertRule: AlertRuleRow;
}

interface FormValues {
  name: string;
  description: string;
  threshold: number;
  conditionOperator: string;
  duration: number;
  severity: 'info' | 'warning' | 'critical';
  enabled: boolean;
  metricType: MetricType;
  tiers: AlertRuleTier[];
}

const severityOptions = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
];

const durationOptions = [
  { value: 5, label: '5 minutes' },
  { value: 10, label: '10 minutes' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
];

const conditionOperatorOptions = [
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '=', label: '=' },
  { value: '>=', label: '>=' },
  { value: '<=', label: '<=' },
];

const metricTypeOptions = [
  { value: 'raw', label: 'Raw Number' },
  { value: 'percentage', label: 'Percentage' },
  { value: 'bytes', label: 'Bytes' },
];

function buildTierPayload(values: FormValues): AlertRuleTiersPayload {
  return {
    description: values.description,
    enabled: values.enabled,
    conditionOperator: values.conditionOperator,
    duration: values.duration,
    tiers: canonicalizeAlertRuleTiers(values.tiers),
  };
}

function buildRulePayload(
  alertRule: AlertRuleRow,
  values: FormValues
): AlertRuleRow {
  return {
    ...alertRule,
    name: values.name,
    description: values.description,
    threshold: values.threshold,
    conditionOperator: values.conditionOperator,
    duration: values.duration,
    severity: values.severity,
    enabled: values.enabled,
    metricType: values.metricType,
  };
}

async function submitAlertRuleUpdate({
  alertRule,
  values,
  useTiers,
  mutateRule,
  mutateTierRule,
}: {
  alertRule: AlertRuleRow;
  values: FormValues;
  useTiers: boolean;
  mutateRule: (rule: AlertRuleRow) => Promise<unknown>;
  mutateTierRule: (payload: AlertRuleTiersPayload) => Promise<unknown>;
}) {
  if (useTiers) {
    await mutateTierRule(buildTierPayload(values));
    return;
  }

  await mutateRule(buildRulePayload(alertRule, values));
}

export function AlertRuleEditForm({ alertRule }: Props) {
  const updateAlertRule = useUpdateAlertRule(alertRule.id);
  const updateAlertRuleTiers = useUpdateAlertRuleTiers(alertRule.id);

  const isSystemRule = alertRule.isInternal;
  const hasConfigurableThreshold =
    alertRule.promqlTemplate?.includes('{{.Threshold}}') ?? true;
  const hasConfigurableOperator =
    (alertRule.promqlTemplate?.includes('{{.Operator}}') ?? true) &&
    !alertRule.operatorFixed;
  const hasConfigurableDuration =
    alertRule.useDurationAsFor !== false ||
    (alertRule.promqlTemplate?.includes('{{.Window}}') ?? false);
  const useTiers = alertRule.useTiers ?? false;

  const initialValues: FormValues = {
    name: alertRule.name,
    description: alertRule.description,
    threshold: alertRule.threshold || 0,
    conditionOperator: alertRule.conditionOperator || '>',
    duration: alertRule.duration || 5,
    severity: alertRule.severity,
    enabled: alertRule.enabled,
    metricType: alertRule.metricType || 'raw',
    tiers: canonicalizeAlertRuleTiers(alertRule.tiers),
  };

  async function handleSubmit(
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) {
    try {
      setSubmitting(true);

      await submitAlertRuleUpdate({
        alertRule,
        values,
        useTiers,
        mutateRule: updateAlertRule.mutateAsync,
        mutateTierRule: updateAlertRuleTiers.mutateAsync,
      });

      notifySuccess('Success', 'Rule updated successfully');
    } catch (error) {
      notifyError('Failure', error, 'Unable to update rule');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      {isSystemRule && (
        <div className="row">
          <div className="col-sm-12">
            <Alert color="info" className="my-4">
              This is a system alert rule. Some fields may be restricted from
              modification.
            </Alert>
          </div>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={createValidationSchema(isSystemRule, useTiers)}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          setFieldValue,
          dirty,
          isSubmitting,
        }) => (
          <Form>
            <FormControl
              label="Name"
              inputId="name-input"
              errors={errors.name && touched.name ? errors.name : undefined}
              required
              tooltip={
                isSystemRule
                  ? 'System alert rule name cannot be changed'
                  : undefined
              }
            >
              <Input
                id="name-input"
                name="name"
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter alert rule name"
                data-cy="alert-rule-name-input"
                readOnly={isSystemRule}
                disabled={isSystemRule}
              />
            </FormControl>

            <FormControl
              label="Description"
              inputId="description-input"
              errors={
                errors.description && touched.description
                  ? errors.description
                  : undefined
              }
            >
              <TextArea
                id="description-input"
                name="description"
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter alert rule description"
                data-cy="alert-rule-description-input"
                rows={3}
              />
            </FormControl>

            {useTiers ? (
              <FormControl
                label="Severity thresholds"
                inputId="tier-config"
                errors={
                  errors.tiers && touched.tiers
                    ? typeof errors.tiers === 'string'
                      ? errors.tiers
                      : undefined
                    : undefined
                }
                tooltip="Configure threshold and enabled state for each severity level"
              >
                <TierConfigPanel
                  tiers={values.tiers}
                  operator={values.conditionOperator}
                  metricType={values.metricType}
                  onChange={(tiers) => setFieldValue('tiers', tiers)}
                />
              </FormControl>
            ) : (
              <>
                {hasConfigurableThreshold && (
                  <ThresholdInput
                    name="threshold"
                    value={values.threshold}
                    onChange={(value) => setFieldValue('threshold', value)}
                    onBlur={() => handleBlur('threshold')}
                    error={
                      errors.threshold && touched.threshold
                        ? errors.threshold
                        : undefined
                    }
                    metricType={values.metricType}
                    data-cy="alert-rule-threshold-input"
                  />
                )}

                <FormControl
                  label="Severity"
                  inputId="severity-input"
                  errors={
                    errors.severity && touched.severity
                      ? errors.severity
                      : undefined
                  }
                  required
                >
                  <Select
                    id="severity-input"
                    options={severityOptions}
                    value={severityOptions.find(
                      (option) => option.value === values.severity
                    )}
                    onChange={(option) =>
                      setFieldValue('severity', option?.value || '')
                    }
                    placeholder="Select severity level"
                    data-cy="alert-rule-severity-select"
                  />
                </FormControl>
              </>
            )}

            {!isSystemRule && (
              <FormControl
                label="Metric Type"
                inputId="metric-type-input"
                errors={
                  errors.metricType && touched.metricType
                    ? errors.metricType
                    : undefined
                }
                required
                tooltip="The type of metric and how the threshold should be interpreted"
              >
                <Select
                  id="metric-type-input"
                  options={metricTypeOptions}
                  value={metricTypeOptions.find(
                    (option) => option.value === values.metricType
                  )}
                  onChange={(option) => {
                    setFieldValue('metricType', option?.value || 'raw');
                  }}
                  placeholder="Select metric type"
                  data-cy="alert-rule-metric-type-select"
                />
              </FormControl>
            )}

            {hasConfigurableOperator && (
              <FormControl
                label="Condition Operator"
                inputId="condition-operator-input"
                errors={
                  errors.conditionOperator && touched.conditionOperator
                    ? errors.conditionOperator
                    : undefined
                }
                required
                tooltip="The operator for the condition"
              >
                <Select
                  id="condition-operator-input"
                  options={
                    useTiers
                      ? conditionOperatorOptions.filter(
                          (o) => o.value === '>' || o.value === '<'
                        )
                      : conditionOperatorOptions
                  }
                  value={conditionOperatorOptions.find(
                    (option) => option.value === values.conditionOperator
                  )}
                  onChange={(option) =>
                    setFieldValue('conditionOperator', option?.value || '')
                  }
                  placeholder="Select condition operator"
                  data-cy="alert-rule-condition-operator-select"
                />
              </FormControl>
            )}

            {hasConfigurableDuration && (
              <FormControl
                label={
                  alertRule.useDurationAsFor === false ? 'Window' : 'Duration'
                }
                inputId="duration-input"
                errors={
                  errors.duration && touched.duration
                    ? errors.duration
                    : undefined
                }
                required
                tooltip={
                  alertRule.useDurationAsFor === false
                    ? 'The time range window used in the PromQL query'
                    : 'How long the condition must be true before the alert fires'
                }
              >
                <Select
                  id="duration-input"
                  options={durationOptions}
                  value={durationOptions.find(
                    (option) => option.value === values.duration
                  )}
                  onChange={(option) =>
                    setFieldValue('duration', option?.value || 5)
                  }
                  placeholder="Select duration"
                  data-cy="alert-rule-duration-select"
                />
              </FormControl>
            )}

            <FormControl label="Enabled" inputId="enabled-input">
              <Switch
                id="enabled-input"
                name="enabled"
                checked={values.enabled}
                onChange={(checked) => setFieldValue('enabled', checked)}
                showStateIcon
                data-cy="alert-rule-enabled-switch"
              />
            </FormControl>

            <div className="row">
              <div className="col-sm-12">
                <LoadingButton
                  type="submit"
                  isLoading={isSubmitting}
                  loadingText="Saving..."
                  disabled={!dirty}
                  data-cy="alert-rule-save-button"
                >
                  Save Rule
                </LoadingButton>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
